/**
 * The user's identity (CU-869fknfmk): brand or broker, their brands and their sites. The first brand is
 * the primary; sites have none (CU-869fmj51h): each one stands for the user in its own country — the
 * `market` the server sends (`SITE_MARKET.market`, shown as "country"; the front never computes one) —
 * and their order means nothing. Pure — no React, no signals, no palette — so `node --test` can run it.
 *
 * Server contract (webback `d4d13dc0`): `GET /api/user/identity` → `{ user: {uid, name}, identity: null |
 * { kind, providers: [{id, label}], sites: [{id, label, market, market_name}] }, options: { providers:
 * [{id, label}], sites: [{id, label, market}] } }`; `PUT` body `{ kind, provider_ids, site_ids }` →
 * `{ user, identity }` = what was saved. Errors `{ error, code, field, ids }`. Site ids are
 * `SITE.id_master_site`.
 */

export const EMPTY_DRAFT = Object.freeze({ kind: null, provider_ids: [], site_ids: [] })

// Past this many sites the field gets a filter, a country count and the List / By country switch (UX D6)
export const LONG_LIST = 12
// Chips shown before "+N more" in a long list (three rows in the mock: 20 + "+45 more" with 65 sites)
export const CHIP_PREVIEW = 20

const toId = (v) => {
	const n = Number(v)
	return Number.isFinite(n) ? n : v
}

const textOrNull = (v) => (v === undefined || v === null || String(v).trim() === '' ? null : String(v))

/**
 * What a chip or a message shows for an id: its name, or "Unknown brand" / "Unknown site" when the server
 * has none — never the bare number (plan Decision 7; QA CU-869fknfmk). Provider names come lowercase
 * from the catalogue (`europcar`): the first letter of each word is capitalised for display.
 */
const titleCase = (s) => String(s).replace(/(^|[\s\-/])([a-z])/g, (_, sep, c) => sep + c.toUpperCase())
export const displayName = (label, id, kind = 'brand') =>
	label == null ? (kind === 'site' ? 'Unknown site' : 'Unknown brand') : (kind === 'site' ? String(label) : titleCase(label))

/** The `identity` of the server's answer as the screen's identity; `null` when the user has none (criterion 8). */
export function fromApi(data) {
	const raw = data?.identity
	if (!raw || typeof raw !== 'object') return null
	const providers = (Array.isArray(raw.providers) ? raw.providers : [])
		.map(p => ({ id: toId(p.id), label: textOrNull(p.label) }))
	const sites = (Array.isArray(raw.sites) ? raw.sites : [])
		.map(s => ({ id: toId(s.id), label: textOrNull(s.label), market: textOrNull(s.market), market_name: textOrNull(s.market_name) }))
	const kind = raw.kind === 'brand' || raw.kind === 'broker' ? raw.kind : null
	if (!kind && !providers.length && !sites.length) return null
	return { kind, providers, sites }
}

/** The pickers' catalogues, from the same answer (`options`), as `{ value, label, market }`. */
export function optionsFromApi(data) {
	const list = (l) => (Array.isArray(l) ? l : [])
	return {
		providers: list(data?.options?.providers).map(o => ({ value: toId(o.id), label: textOrNull(o.label) })),
		sites: list(data?.options?.sites).map(o => ({ value: toId(o.id), label: textOrNull(o.label), market: textOrNull(o.market) })),
	}
}

/** Whose identity it is: `{ uid, name }` of the session user (the slave's while a master views it). */
export const userFromApi = (data) => (data?.user ? { uid: data.user.uid ?? null, name: textOrNull(data.user.name) } : null)

/** Saved identity → editable draft, ids in their saved order (the first brand is the primary). */
export function toDraft(identity) {
	if (!identity) return { ...EMPTY_DRAFT, provider_ids: [], site_ids: [] }
	return {
		kind: identity.kind ?? null,
		provider_ids: (identity.providers || []).map(p => p.id),
		site_ids: (identity.sites || []).map(s => s.id),
	}
}

/**
 * Names for every id the screen can show: the catalogue (`optionsFromApi`) joined with what is saved.
 * The saved one wins: it carries the master's name and the market's name. An id without a name maps to
 * `null`; the screen shows `#id` for it (`displayName`).
 */
export function labelsFrom(identity, options = {}) {
	const providers = new Map()
	const sites = new Map()
	for (const o of options.providers || []) providers.set(toId(o.value), textOrNull(o.label))
	for (const o of options.sites || []) sites.set(toId(o.value), { label: textOrNull(o.label), market: textOrNull(o.market), market_name: null })
	for (const p of identity?.providers || []) {
		providers.set(p.id, p.label ?? providers.get(p.id) ?? null)
	}
	for (const s of identity?.sites || []) {
		const known = sites.get(s.id) || {}
		sites.set(s.id, {
			label: s.label ?? known.label ?? null,
			market: s.market ?? known.market ?? null,
			market_name: s.market_name ?? null,
		})
	}
	return { providers, sites }
}

/** Added at the end, never twice. */
export const addId = (list, id) => (list.includes(id) ? list : [...list, id])
/** Brands only: to the front; the rest keep their order. */
export const makePrimary = (list, id) => (list.includes(id) ? [id, ...list.filter(x => x !== id)] : list)
/** Out; for brands, removing the first makes the next one the primary by construction. */
export const removeId = (list, id) => list.filter(x => x !== id)

/** Changes the kind and keeps the brands: going back to Brand brings them back (UX D8). */
export const setKind = (draft, kind) => ({ ...draft, kind })

/** As a broker with brands still in the draft: what saving would drop, for the amber note. */
export function pendingBrandRemoval(draft, labels) {
	if (draft?.kind !== 'broker' || !draft.provider_ids?.length) return null
	return {
		count: draft.provider_ids.length,
		names: draft.provider_ids.map(id => displayName(labels?.providers?.get(id) ?? null, id)),
	}
}

/** The `PUT` body. A broker has no brands. */
export function toPayload(draft) {
	return {
		kind: draft.kind,
		provider_ids: draft.kind === 'broker' ? [] : [...draft.provider_ids],
		site_ids: [...draft.site_ids],
	}
}

export const VALIDATION = {
	kind: 'Choose Brand or Broker to start',
	brands: 'Add at least one brand. The first one is “you”.',
	sites: 'Add at least one site',
}

/** What blocks the save, in the order the user meets the fields (criterion 6). */
export function validateDraft(draft) {
	if (!draft?.kind) return { ok: false, field: 'kind', message: VALIDATION.kind }
	if (draft.kind === 'brand' && !draft.provider_ids.length) return { ok: false, field: 'brands', message: VALIDATION.brands }
	if (draft.kind === 'broker' && !draft.site_ids.length) return { ok: false, field: 'sites', message: VALIDATION.sites }
	return { ok: true, field: null, message: null }
}

/** The footer's version of the same message. */
export function footerMissing(field) {
	if (field === 'brands') return "Can't save yet: add at least one brand"
	if (field === 'sites') return "Can't save yet: add at least one site"
	return VALIDATION.kind
}

/** What would be saved, so a broker's leftover brands do not count as a difference. */
export const canonical = (draft) => toPayload(draft || EMPTY_DRAFT)

const sameSet = (a, b) => a.length === b.length && a.every(x => b.includes(x))

/**
 * Changes as the user thinks of them: kind · primary brand · the set of brands · the set of sites (sites
 * have no primary). Moving a brand to the front is one change (the set did not change), adding one is one more.
 */
export function changeCount(saved, draft) {
	const a = canonical(toDraft(saved))
	const b = canonical(draft)
	let n = 0
	if ((a.kind ?? null) !== (b.kind ?? null)) n++
	if (a.provider_ids[0] !== b.provider_ids[0]) n++
	if (!sameSet(a.provider_ids, b.provider_ids)) n++
	if (!sameSet(a.site_ids, b.site_ids)) n++
	return n
}

export const isDirty = (saved, draft) => changeCount(saved, draft) > 0

const regionNames = (() => {
	try { return new Intl.DisplayNames(['en'], { type: 'region' }) } catch { return null }
})()
// Codes the backend uses that are not ISO 3166 regions
const MARKET_ALIASES = { UK: 'GB', EL: 'GR' }

/** "DE" → "Germany"; the code itself when there is no name for it. */
export function marketName(code) {
	if (!code) return null
	const upper = String(code).toUpperCase()
	try {
		const name = regionNames?.of(MARKET_ALIASES[upper] || upper)
		return name && name !== upper ? name : upper
	} catch {
		return upper
	}
}

const fold = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export const NO_COUNTRY = 'No country'

/**
 * A site matches by its name, its country code or its country's name, without case or accents; a site
 * with no country also matches "no country".
 */
export function matchesQuery(site, query) {
	const q = fold(query).trim()
	if (!q) return true
	const values = [site?.label, site?.market, site?.market_name, marketName(site?.market)]
	if (!site?.market) values.push(NO_COUNTRY)
	return values.some(v => fold(v).includes(q))
}

const countryOf = (site) => (site.market ? (site.market_name || marketName(site.market)) : null)
const rank = (site) => (site.label == null ? 2 : site.market ? 0 : 1)

/**
 * The one order every list of sites uses (UX Obs. 6-7): by country name, then by site name; the named
 * sites with no country after them; "Unknown site" (no name) last. A new copy; the input order is irrelevant.
 */
export function sortSites(items) {
	return [...(items || [])].sort((a, b) => rank(a) - rank(b)
		|| String(countryOf(a) ?? '').localeCompare(String(countryOf(b) ?? ''))
		|| String(a.label ?? '').localeCompare(String(b.label ?? ''))
		|| String(a.id).localeCompare(String(b.id), undefined, { numeric: true }))
}

/** The long list's counter: distinct countries, and the named sites with no country. */
export function countryCount(items) {
	const countries = new Set()
	let noCountry = 0
	for (const s of items || []) {
		if (s.market) countries.add(String(s.market).toUpperCase())
		else if (s.label != null) noCountry++
	}
	return { countries: countries.size, noCountry }
}

/** Sites grouped by country, countries by name, sites without a country last under "No country". */
export function groupByCountry(sites) {
	const groups = new Map()
	for (const site of sites) {
		const key = site.market || null
		if (!groups.has(key)) groups.set(key, { market: key, market_name: key ? (site.market_name || marketName(key)) : NO_COUNTRY, sites: [] })
		groups.get(key).sites.push(site)
	}
	return [...groups.values()].sort((a, b) => {
		if (!a.market) return 1
		if (!b.market) return -1
		return a.market_name.localeCompare(b.market_name)
	})
}

/** The first `CHIP_PREVIEW` chips and how many are hidden behind "+N more". */
export function preview(list, expanded) {
	if (expanded || list.length <= CHIP_PREVIEW) return { shown: list, more: 0 }
	return { shown: list.slice(0, CHIP_PREVIEW), more: list.length - CHIP_PREVIEW }
}

/**
 * The footer's line, in priority order: the save error · what is missing · N changes · saved just now
 * · at rest. `tone` is `error`, `dirty`, `ok` or `muted`.
 */
export function footerHint({ saved, dirty, changes, error, savedJustNow, validation }) {
	if (error) return { tone: 'error', text: `Could not save (${error.status || 'network error'}). Nothing was changed — your edits are still here` }
	if (dirty && validation && !validation.ok) return { tone: 'error', text: footerMissing(validation.field) }
	if (dirty) return { tone: 'dirty', count: changes, text: 'Not saved yet — Iris keeps using the saved identity' }
	if (savedJustNow) return { tone: 'ok', text: 'Saved just now' }
	if (!saved) return { tone: 'muted', text: VALIDATION.kind }
	return { tone: 'ok', text: 'In use by Iris' }
}

/** The success toast's second line: who "you" is from now on (a brand: its primary; a broker: its sites). */
export function savedSummary(identity) {
	if (!identity) return null
	if (identity.kind === 'brand') {
		const brand = identity.providers?.[0]?.label
		return brand ? `Iris uses ${brand} as “you” from your next question.` : null
	}
	if (identity.kind === 'broker' && identity.sites?.length) return 'Iris uses your sites as “you”, each in its own country, from your next question.'
	return null
}

// The server's field names → the editor's fields
const SERVER_FIELDS = { kind: 'kind', provider_ids: 'brands', providers: 'brands', site_ids: 'sites', sites: 'sites' }
const CODE_FIELDS = { kind_required: 'kind', brands_required: 'brands', unknown_provider: 'brands', sites_required: 'sites', site_not_followed: 'sites' }

/**
 * A rejected save (`{ status, code, field, ids, message }`) as the message next to its field, or
 * `null` when it belongs to no field (400, 5xx, network: the toast and the footer say it).
 */
export function serverFieldError(error, labels) {
	if (!error || error.status !== 422) return null
	const field = SERVER_FIELDS[error.field] || CODE_FIELDS[error.code] || null
	if (!field) return null
	const ids = Array.isArray(error.ids) ? error.ids.map(toId) : []
	const names = (map) => ids.map(id => displayName(map?.get(id) ?? null, id)).join(', ')
	const siteNames = ids.map(id => displayName(labels?.sites?.get(id)?.label ?? null, id, 'site')).join(', ')
	let message
	if (error.code === 'site_not_followed') message = `Not a site your account follows${ids.length ? `: ${siteNames}` : ''}. Remove it to save.`
	else if (error.code === 'unknown_provider') message = `Not a brand your account can pick${ids.length ? `: ${names(labels?.providers)}` : ''}. Remove it to save.`
	else message = error.message || (field === 'kind' ? VALIDATION.kind : field === 'brands' ? VALIDATION.brands : VALIDATION.sites)
	return { field, message }
}
